import React, { useState } from "react";
import { useEffect } from "react";
import { ReactSVG } from "react-svg";

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

import Steps from "../../Steps";
import Button from "../../../../common/Button";
import ChartNav from "../../../../common/ChartNav";
import Medications from "../../medications/medications";
import { Link, useParams } from "react-router-dom";
import {
  highRiskProfessionEnum,
  irritantRhinitisEnum,
  occupationalRhinitisEnum,
  structuralOrMechanicalConditionsEnum,
  systemicConditionsEnum,
} from "./data";
import CustomInput from "../../../../common/CustomInput";
import EndServiceModel from "../../../../common/EndServiceModel";
import NewToolTip from "../../../../common/New-ToolTip";
import OccupationalRhinitisToolTip from "../../../../common/tooltip/minor-ailments/occupational-rhinitis";
import { useDispatch, useSelector } from "react-redux";
import {
  setClinicalServiceSteps,
  updateClinicalService,
} from "../../../../../redux/modules/clinical-services/actions";
import {
  AlertConfirmationTypes,
  MinorAilmentStep,
} from "../../../../../helpers/constant";
import { scrollX } from "../../../../../helpers/functions";
import SubNavigation from "../../common/sub-navigation";
import { getValidStep, updateStepsValidity } from "../../../text/data";

const DifferentialAssessment = () => {
  const { clinicalServiceObj, clinicalServiceSteps } = useSelector(
    (state) => state.clinicalService
  );
  const { currentStep, currentSubStep } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const { serviceId } = useParams();
  const dispatch = useDispatch();

  const [irritantRhinitis, setIrritantRhinitis] = useState([]);
  const [irritantRhinitisComments, setIrritantRhinitisComments] = useState("");
  const [occupationalRhinitis, setOccupationalRhinitis] = useState("");
  const [highRiskProfession, setHighRiskProfession] = useState([]);
  const [
    structuralOrMechanicalConditions,
    setStructuralOrMechanicalConditions,
  ] = useState([]);
  const [
    structuralOrMechanicalConditionsComments,
    setStructuralOrMechanicalConditionsComments,
  ] = useState("");
  const [systemicConditions, setSystemicConditions] = useState([]);
  const [systemicConditionsComments, setSystemicConditionsComments] =
    useState("");

  const [differentialAnalysis, setDifferentialAnalysis] = useState(false);
  const [openDifferentialAssessmentAlert, setOpenDifferentialAssessmentAlert] =
    useState(false);
  const [openNoDiagnosisDialog, setOpenNoDiagnosisDialog] = useState(false);
  const [continueService, setContinueService] = useState(false);
  const [completeSubStep, setCompleteSubStep] = useState(false);
  const [completeDAAlert, setCompleteDAAlert] = useState(false);
  const [clickedEndService, setClickedEndService] = useState(false);
  const [skipSelected, setSkipSelected] = useState(false);
  const [alertConfirmation, setAlertConfirmation] = useState(0);
  const [alertExplaination, setAlertExplaination] = useState("");

  useEffect(() => {
    if (clickedEndService) {
      onNext();
      setClickedEndService(false);
    }
  }, [clinicalServiceSteps, clickedEndService]);

  const { showReferral } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const [showRef, setShowRef] = useState(showReferral ?? false);

  useEffect(() => {
    if (
      clinicalServiceObj.initialAssessment.initialAssessmentForm
        .differentialAssessment !== null &&
      clinicalServiceObj.initialAssessment.initialAssessmentForm
        .differentialAssessment !== undefined
    ) {
      let differentialAssessment =
        clinicalServiceObj.initialAssessment.initialAssessmentForm
          .differentialAssessment;
      setIrritantRhinitis(differentialAssessment.irritantRhinitis);
      setIrritantRhinitisComments(
        differentialAssessment.irritantRhinitisComments
      );
      setOccupationalRhinitis(
        differentialAssessment.occupationalRhinitisIndicator
      );
      setHighRiskProfession(differentialAssessment.highRiskProfession);
      setStructuralOrMechanicalConditions(
        differentialAssessment.structuralOrMechanicalConditions
      );
      setStructuralOrMechanicalConditionsComments(
        differentialAssessment.structuralOrMechanicalConditionsComments
      );
      setSystemicConditions(differentialAssessment.systemicConditions);
      setSystemicConditionsComments(
        differentialAssessment.systemicConditionsComments
      );
      setCompleteDAAlert(true);
      setDifferentialAnalysis(true);
      scrollX("differentialAssessment");
    }
  }, [clinicalServiceObj.initialAssessment.initialAssessmentForm]);

  // Related to each question card
  const irritantRhinitisMultiSelection = (value) => {
    if (value === "none") {
      setIrritantRhinitis(["none"]);
    } else {
      setIrritantRhinitis((prev) => {
        if (prev.includes(value)) {
          return prev.filter((item) => item !== value);
        } else {
          if (prev.includes("none")) {
            return [value];
          } else {
            return [...prev, value];
          }
        }
      });
    }
  };

  const extraQuestionsOccupationalRhinitis = () => {
    return (
      occupationalRhinitis === "Yes" && (
        <div className="flex flex-col gap-4">
          <p className="text-lg text-gray-800">
            <span className="font-semibold">
              {highRiskProfessionEnum.question}
            </span>
            <br />
            {highRiskProfessionEnum.subquestion}
          </p>
          <div className="flex flex-col gap-2">
            {highRiskProfessionEnum.options.map((option) => {
              return (
                <>
                  <button
                    onClick={() => highRiskProfessionMultiSelect(option.value)}
                    className={clsx(
                      `font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                            text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600`,
                      highRiskProfession?.includes(option.value) &&
                        "bg-gray-20 border-gray-600",
                      !highRiskProfession?.includes(option.value) &&
                        "bg-white border-gray-100"
                    )}
                  >
                    <p className="font-semibold text-body-md flex justify-start items-start">
                      {option.header}
                    </p>
                    <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                      {option.subHeader}
                    </p>
                  </button>
                </>
              );
            })}
          </div>
        </div>
      )
    );
  };

  const highRiskProfessionMultiSelect = (value) => {
    if (value === "none") {
      setHighRiskProfession(["none"]);
    } else {
      setHighRiskProfession((prev) => {
        if (prev.includes(value)) {
          return prev.filter((item) => item !== value);
        } else {
          if (prev.includes("none")) {
            return [value];
          } else {
            return [...prev, value];
          }
        }
      });
    }
  };

  const structuralOrMechanicalConditionsMultiSelection = (value) => {
    if (value === "none") {
      setStructuralOrMechanicalConditions(["none"]);
    } else {
      setStructuralOrMechanicalConditions((prev) => {
        if (prev.includes(value)) {
          return prev.filter((item) => item !== value);
        } else {
          if (prev.includes("none")) {
            return [value];
          } else {
            return [...prev, value];
          }
        }
      });
    }
  };

  const systemicConditionsMultiSelection = (value) => {
    if (value === "none") {
      setSystemicConditions(["none"]);
    } else {
      setSystemicConditions((prev) => {
        if (prev.includes(value)) {
          return prev.filter((item) => item !== value);
        } else {
          if (prev.includes("none")) {
            return [value];
          } else {
            return [...prev, value];
          }
        }
      });
    }
  };

  // Additional comments
  const customIdentifyIrritantComponent = (input) => {
    return (
      <CustomInput
        name={input.name}
        className="col-span-12 mt-9"
        label={input.label}
        placeholder={input.placeholder}
        type={input.type}
        value={irritantRhinitisComments}
        onChange={(e) => setIrritantRhinitisComments(e.target.value)}
      />
    );
  };

  const customStructuralOrMechanicalConditionsComponent = (input) => {
    return (
      <CustomInput
        name={input.name}
        className="col-span-12 mt-9"
        label={input.label}
        placeholder={input.placeholder}
        type={input.type}
        value={structuralOrMechanicalConditionsComments}
        onChange={(e) =>
          setStructuralOrMechanicalConditionsComments(e.target.value)
        }
      />
    );
  };

  const customSystemicConditionsComponent = (input) => {
    return (
      <CustomInput
        name={input.name}
        className="col-span-12 mt-9"
        label={input.label}
        placeholder={input.placeholder}
        type={input.type}
        value={systemicConditionsComments}
        onChange={(e) => setSystemicConditionsComments(e.target.value)}
      />
    );
  };

  const handleAnalysis = () => {
    if (!continueService) {
      if (
        irritantRhinitis[0] === "none" &&
        occupationalRhinitis !== "Yes" &&
        structuralOrMechanicalConditions[0] === "none" &&
        systemicConditions[0] === "none"
      ) {
        setOpenNoDiagnosisDialog(true);
        setDifferentialAnalysis(true);
      } else {
        setOpenDifferentialAssessmentAlert(true);
      }
    } else setDifferentialAnalysis(true);
  };

  const renderPopupContent = () => {
    if (
      occupationalRhinitis === "Yes" &&
      highRiskProfession.length !== 0 &&
      irritantRhinitis[0] === "none" &&
      structuralOrMechanicalConditions[0] === "none" &&
      systemicConditions[0] === "none"
    ) {
      return (
        <div className="flex flex-col gap-4">
          <p className="font-semibold text-gray-600 text-body-md">
            Occupational Rhinitis Identified
          </p>
          <p className="text-body-sm text-gray-600">
            Allergy symptoms may have started in the work place.
          </p>
          <p className="text-body-sm text-gray-600 ">
            {" "}
            <span className="capitalize">
              {" "}
              {highRiskProfession.join(", ")}
            </span>{" "}
            - HIGH RISK occupation(s) has been identified.
          </p>
        </div>
      );
    } else if (
      occupationalRhinitis !== "Yes" &&
      highRiskProfession.length === 0 &&
      irritantRhinitis[0] === "none" &&
      structuralOrMechanicalConditions[0] === "none" &&
      systemicConditions[0] !== "none"
    ) {
      return (
        <div className="flex flex-col gap-4">
          <p className="font-semibold text-gray-600 text-body-md">
            Systemic Condition Identified
          </p>
          <p className="text-body-sm text-gray-600 ">
            {" "}
            <span className="capitalize">
              {systemicConditionsEnum.options
                .filter((option) => systemicConditions.includes(option.value))
                .map((item) => item.header)
                .join(", ")}
            </span>
          </p>
        </div>
      );
    } else if (
      occupationalRhinitis !== "Yes" &&
      highRiskProfession.length === 0 &&
      irritantRhinitis[0] === "none" &&
      structuralOrMechanicalConditions[0] !== "none" &&
      systemicConditions[0] === "none"
    ) {
      return (
        <div className="flex flex-col gap-4">
          <p className="font-semibold text-gray-600 text-body-md">
            Structural or Mechanical Condition Identified
          </p>
          <p className="text-body-sm text-gray-600 ">
            {" "}
            <span className="capitalize">
              {structuralOrMechanicalConditionsEnum.options
                .filter((option) =>
                  structuralOrMechanicalConditions.includes(option.value)
                )
                .map((item) => item.header)
                .join(", ")}
            </span>
          </p>
        </div>
      );
    } else if (
      occupationalRhinitis !== "Yes" &&
      highRiskProfession.length === 0 &&
      irritantRhinitis[0] !== "none" &&
      structuralOrMechanicalConditions[0] === "none" &&
      systemicConditions[0] === "none"
    ) {
      return (
        <div className="flex flex-col gap-4">
          <p className="font-semibold text-gray-600 text-body-md">
            Irritant Rhinitis Identified
          </p>
          <p className="text-body-sm text-gray-600 ">
            {" "}
            <span className="capitalize">
              {irritantRhinitisEnum.options
                .filter((option) => irritantRhinitis.includes(option.value))
                .map((item) => item.header)
                .join(", ")}
            </span>
          </p>
          <p className="text-body-sm text-gray-600">
            Avoid the IRRITANT when possible.
          </p>
        </div>
      );
    } else {
      return (
        <div className="flex flex-col gap-4">
          <p className="font-semibold text-gray-600 text-body-md">
            Differential Diagnoses Identified
          </p>
          <div className="flex flex-col space-y-4">
            {irritantRhinitis[0] !== "none" && (
              <div className="flex flex-col border-b-[1px] border-b-gray-100 ">
                <p className="font-semibold text-gray-600 text-body-md">
                  Irritant Rhinitis Identified
                </p>
                <p className="text-body-sm text-gray-600 ">
                  <span className="capitalize">
                    {irritantRhinitisEnum.options
                      .filter((option) =>
                        irritantRhinitis.includes(option.value)
                      )
                      .map((item) => item.header)
                      .join(", ")}
                  </span>
                </p>
              </div>
            )}
            {occupationalRhinitis === "Yes" &&
              highRiskProfession.length !== 0 && (
                <div className="flex flex-col border-b-[1px] border-b-gray-100 ">
                  <p className="font-semibold text-gray-600 text-body-md">
                    Occupational Rhinitis Identified
                  </p>
                  <p className="text-body-sm text-gray-600">
                    <span className="capitalize">
                      {" "}
                      {highRiskProfession.join(", ")}
                    </span>
                  </p>
                </div>
              )}
            {structuralOrMechanicalConditions[0] !== "none" && (
              <div className="flex flex-col border-b-[1px] border-b-gray-100 ">
                <p className="font-semibold text-gray-600 text-body-md">
                  Structural or Mechanical Condition Identified
                </p>
                <p className="text-body-sm text-gray-600 ">
                  <span className="capitalize">
                    {structuralOrMechanicalConditionsEnum.options
                      .filter((option) =>
                        structuralOrMechanicalConditions.includes(option.value)
                      )
                      .map((item) => item.header)
                      .join(", ")}
                  </span>
                </p>
              </div>
            )}
            {systemicConditions[0] !== "none" && (
              <div className="flex flex-col border-b-[1px] border-b-gray-100 ">
                <p className="font-semibold text-gray-600 text-body-md">
                  Systemic Condition Identified
                </p>
                <p className="text-body-sm text-gray-600 ">
                  <span className="capitalize">
                    {systemicConditionsEnum.options
                      .filter((option) =>
                        systemicConditions.includes(option.value)
                      )
                      .map((item) => item.header)
                      .join(", ")}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }
  };

  const updateStepsVisibility = async (disable = false) => {
    const updatedServiceSteps = updateStepsValidity(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      disable
    );
    await dispatch(setClinicalServiceSteps(updatedServiceSteps));
  };

  //Move Back and Forth
  const onNext = () => {
    if (currentStep <= clinicalServiceSteps.length) {
      const { nextStep, nextSubStep } = getValidStep(
        clinicalServiceSteps,
        currentStep,
        currentSubStep,
        1
      );

      const updatedSteps = {
        differentialAssessment: {
          irritantRhinitis: irritantRhinitis,
          irritantRhinitisComments: irritantRhinitisComments,
          occupationalRhinitisIndicator: occupationalRhinitis,
          highRiskProfession: highRiskProfession,
          structuralOrMechanicalConditions: structuralOrMechanicalConditions,
          structuralOrMechanicalConditionsComments:
            structuralOrMechanicalConditionsComments,
          systemicConditions: systemicConditions,
          systemicConditionsComments: systemicConditionsComments,
          differentialConfirmationType: alertConfirmation,
          differentialExplaination: alertExplaination,
        },
        currentStep: nextStep,
        currentSubStep: nextSubStep,
        showReferral: showRef,
        serviceSteps: clinicalServiceSteps,
      };
      dispatch(updateClinicalService(serviceId, updatedSteps));
    }
  };

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      -1
    );
    const updatedSteps = { currentStep: nextStep, currentSubStep: nextSubStep };
    dispatch(updateClinicalService(serviceId, updatedSteps));
  };

  useEffect(() => {
    if (skipSelected) return;

    if (
      irritantRhinitis.length > 0 &&
      occupationalRhinitis !== "Yes" &&
      structuralOrMechanicalConditions.length > 0 &&
      systemicConditions.length > 0
    ) {
      setCompleteSubStep(true);
    } else if (
      irritantRhinitis.length > 0 &&
      occupationalRhinitis === "Yes" &&
      highRiskProfession.length > 0 &&
      structuralOrMechanicalConditions.length > 0 &&
      systemicConditions.length > 0
    ) {
      setCompleteSubStep(true);
    } else {
      setCompleteSubStep(false);
    }
  }, [
    highRiskProfession,
    structuralOrMechanicalConditions,
    occupationalRhinitis,
    systemicConditions,
    irritantRhinitis,
    skipSelected,
  ]);

  const resetAnalysis = () => {
    setDifferentialAnalysis(false);
    setCompleteSubStep(false);
    setContinueService(false);
    setCompleteDAAlert(false);
    setSkipSelected(false);
  };

  const setNotApplicable = () => {
    setIrritantRhinitis(["none"]);
    setIrritantRhinitisComments(null);
    setOccupationalRhinitis("Does not apply");
    setHighRiskProfession([]);
    setStructuralOrMechanicalConditions(["none"]);
    setStructuralOrMechanicalConditionsComments(null);
    setSystemicConditions(["none"]);
    setSystemicConditionsComments(null);
    // Mark the card as completed because user intentionally skipped this step
    setSkipSelected(true);
    setDifferentialAnalysis(true);
    setCompleteSubStep(true);
    setContinueService(true);
    setCompleteDAAlert(true);
    setTimeout(() => {
      scrollX("differentialAssessment", 1200);
    }, 300);
  };

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications />
          <div className="flex flex-1 w-full h-[668px] overflow-y-auto scroll-smooth ">
            <div className="w-full gap-5">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-xs-display font-bold text-teal-300">
                  Differential Assessment
                  <button className="text-[18px] font-semibold text-teal-300 pl-4">
                    <span
                      onClick={() => {
                        setNotApplicable();
                      }}
                      className="underline"
                    >
                      Select to Skip
                    </span>{" "}
                     - if does not apply
                  </button>
                </h4>

                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              <div
                className="grid grid-flow-col auto-cols-[480px] gap-6 scroll-smooth overflow-auto"
                id="differentialAssessment"
              >
                <div className={``}>
                  <div className="h-[595px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background-2.png)`,
                      }}
                    >
                      <h3 className="text-xl font-bold text-teal-600 ">
                        {irritantRhinitisEnum.header}
                      </h3>
                    </div>
                    <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                      <div className="flex flex-col gap-4">
                        <p className="text-lg h-[58px] font-semibold text-gray-800">
                          {irritantRhinitisEnum.question}
                        </p>
                        <div className="flex flex-col gap-2">
                          {irritantRhinitisEnum.options.map((option, key) => {
                            return (
                              <button
                                onClick={() => {
                                  irritantRhinitisMultiSelection(option.value);
                                  scrollX("differentialAssessment");
                                  resetAnalysis();
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                                  text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                                    irritantRhinitis?.includes(option.value)
                                      ? "bg-gray-20 border-gray-600 !gap-0"
                                      : "bg-white border-gray-100"
                                  }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.header}
                                </p>
                                <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                  {option.subHeader}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                        {customIdentifyIrritantComponent(
                          irritantRhinitisEnum.customInput
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={``}>
                  <div className="h-[595px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background-2.png)`,
                      }}
                    >
                      <h3 className="text-xl font-bold text-teal-600 ">
                        {occupationalRhinitisEnum.header}
                      </h3>
                    </div>
                    <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                      <div className="flex flex-col gap-4">
                        <p className="text-lg h-[58px] font-semibold text-gray-800">
                          {occupationalRhinitisEnum.question}
                          <ReactSVG
                            src="/icons/information-dark.svg"
                            className="w-5 h-5 ml-1  inline-block"
                            data-tooltip-id="occupationalRhinitis"
                          />
                        </p>
                        <NewToolTip
                          id="occupationalRhinitis"
                          render={() => <OccupationalRhinitisToolTip />}
                        />
                        <div className="flex flex-col gap-2">
                          {occupationalRhinitisEnum.options.map((option) => {
                            return (
                              <button
                                onClick={() => {
                                  setOccupationalRhinitis(option.value);
                                  if (option.value === "Does not apply")
                                    setHighRiskProfession([]);
                                  resetAnalysis();
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                                  text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                                    occupationalRhinitis === option.value
                                      ? "bg-gray-20 border-gray-600 !gap-0"
                                      : "bg-white border-gray-100"
                                  }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.header}
                                </p>
                                <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                  {option.subHeader}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                        {extraQuestionsOccupationalRhinitis()}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={``}>
                  <div className="h-[595px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background-2.png)`,
                      }}
                    >
                      <h3 className="text-xl font-bold text-teal-600 ">
                        {structuralOrMechanicalConditionsEnum.header}
                      </h3>
                    </div>
                    <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                      <div className="flex flex-col gap-4">
                        <p className="text-lg h-[58px] font-semibold text-gray-800">
                          {structuralOrMechanicalConditionsEnum.question}
                        </p>
                        <div className="flex flex-col gap-2">
                          {structuralOrMechanicalConditionsEnum.options.map(
                            (option) => {
                              return (
                                <button
                                  onClick={() => {
                                    structuralOrMechanicalConditionsMultiSelection(
                                      option.value
                                    );
                                    scrollX("differentialAssessment");
                                    resetAnalysis();
                                  }}
                                  className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px] text-gray-800 
                                  hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                                    structuralOrMechanicalConditions?.includes(
                                      option.value
                                    )
                                      ? "bg-gray-20 border-gray-600 !gap-0"
                                      : "bg-white border-gray-100"
                                  }`}
                                >
                                  <p className="font-semibold text-body-md flex justify-start items-start text-left">
                                    {option.header}
                                  </p>
                                  <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                    {option.subHeader}
                                  </p>
                                </button>
                              );
                            }
                          )}
                        </div>
                        {customStructuralOrMechanicalConditionsComponent(
                          structuralOrMechanicalConditionsEnum.customInput
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={``}>
                  <div className="h-[595px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background-2.png)`,
                      }}
                    >
                      <h3 className="text-xl font-bold text-teal-600 ">
                        {systemicConditionsEnum.header}
                      </h3>
                    </div>
                    <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                      <div className="flex flex-col gap-4">
                        <p className="text-lg h-[58px] font-semibold text-gray-800">
                          {systemicConditionsEnum.question}
                        </p>
                        <div className="flex flex-col gap-2">
                          {systemicConditionsEnum.options.map((option) => {
                            return (
                              <button
                                onClick={() => {
                                  systemicConditionsMultiSelection(
                                    option.value
                                  );
                                  scrollX("differentialAssessment");
                                  resetAnalysis();
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                                  text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                                    systemicConditions?.includes(option.value)
                                      ? "bg-gray-20 border-gray-600 !gap-0"
                                      : "bg-white border-gray-100"
                                  }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.header}
                                </p>
                                <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                  {option.subHeader}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                        {customSystemicConditionsComponent(
                          systemicConditionsEnum.customInput
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {!skipSelected && (
                  <div
                    className={`h-[585px] rounded-[4px] overflow-hidden flex flex-col relative 
                    ${completeSubStep && "border border-gray-100"}`}
                  >
                    {completeSubStep && (
                      <>
                        {/* <button
                          className="m-2 absolute h-4 w-auto rounded-full bg-REELTeal border-REELTeal hover:bg-teal-200  text-white inline-flex border transition-all duration-300 items-center font-semibold justify-center px-2 py-4"
                          onClick={() => {
                            setContinueService(false);
                            scrollBack();
                            setDifferentialAnalysis(false);
                            setCompleteDAAlert(false);
                          }}
                        >
                          <ChevronLeftIcon className="w-4 h-4 text-white" />
                        </button> */}
                        <div className="m-8 h-[420px] w-[420px] bg-blue-100 rounded-full flex items-center justify-center bg-opacity-40">
                          <ReactSVG
                            src={"/icons/analysis-pad.svg"}
                            className={``}
                          />
                        </div>
                        <div className="mx-7 my-5">
                          {!differentialAnalysis && (
                            <Button
                              className="w-full !px-2 !text-xl"
                              type="primary"
                              text="Differential Analysis"
                              onClick={() => {
                                handleAnalysis();
                              }}
                            />
                          )}
                          {differentialAnalysis && (
                            <h4 className="text-xl text-REELTeal font-bold items-center justify-center flex">
                              Differential Analysis Complete
                            </h4>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
                <EndServiceModel
                  open={openDifferentialAssessmentAlert}
                  title="Differential Assessment Alert"
                  content={() => renderPopupContent()}
                  handleClose={() => {
                    setAlertConfirmation(0);
                    setAlertExplaination("");
                    setOpenDifferentialAssessmentAlert(false);
                    setDifferentialAnalysis(false);
                    setContinueService(false);
                  }}
                  handleContinue={(ref, exp) => {
                    setAlertConfirmation(
                      ref
                        ? AlertConfirmationTypes.ContinueWithRefer
                        : AlertConfirmationTypes.ContinueWithNoReferral
                    );
                    setAlertExplaination(exp);
                    if (ref !== undefined) {
                      setShowRef(ref);
                    }
                    setOpenDifferentialAssessmentAlert(false);
                    setDifferentialAnalysis(true);
                    setContinueService(true);
                    setCompleteDAAlert(true);
                    updateStepsVisibility(false);
                  }}
                  handleEndService={() => {
                    setShowRef(true);
                    setAlertConfirmation(AlertConfirmationTypes.EndService);
                    setAlertExplaination("");
                    if (
                      occupationalRhinitis === "Yes" ||
                      structuralOrMechanicalConditions[0] !== "none" ||
                      systemicConditions[0] !== "none"
                    ) {
                      setOpenDifferentialAssessmentAlert(false);
                      setDifferentialAnalysis(false);
                    } else {
                      setOpenDifferentialAssessmentAlert(false);
                    }
                    updateStepsVisibility(true);
                    setClickedEndService(true);
                  }}
                />
                <EndServiceModel
                  open={openNoDiagnosisDialog}
                  showEndServiceAndRef={false}
                  showOTCConsultAndRef={false}
                  className="w-[40%]"
                  title="Differential Assessment Alert"
                  content={() => (
                    <div className="flex flex-col">
                      <p className="text-gray-600 text-body-md">
                        No Differential Diagnosis Identified
                      </p>
                    </div>
                  )}
                  handleClose={() => {
                    setContinueService(false);
                    setOpenNoDiagnosisDialog(false);
                    setAlertConfirmation(0);
                    setAlertExplaination("");
                  }}
                  handleContinue={(ref) => {
                    setAlertConfirmation(0);
                    setAlertExplaination("");
                    if (ref !== undefined) {
                      setShowRef(ref);
                    }
                    setOpenNoDiagnosisDialog(false);
                    setDifferentialAnalysis(true);
                    setContinueService(true);
                    setCompleteDAAlert(true);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Nav */}
      <div className="flex items-center justify-between space-x-4  rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => {
              onPrevious();
            }}
          />
          <Button
            type="primary"
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px]  ${
              !completeDAAlert && "opacity-40 pointer-events-none"
            }`}
            onClick={() => {
              onNext();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DifferentialAssessment;
